import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import {
  fetchSharedUsers,
  generateSharedLink,
  getLatestSharedLink,
} from "../services/sharedService";
import "./ShareSurveyModal.css";

/**
 * Props:
 * - surveyId (required)
 * - surveyTitle (optional) => to display at the top
 * - onClose => closes the modal
 */
const ShareSurveyModal = ({ surveyId, surveyTitle, onClose }) => {
  const [link, setLink] = useState("");
  const [expiresAt, setExpiresAt] = useState(null);
  const [loading, setLoading] = useState(false);

  const [expiryChoice, setExpiryChoice] = useState("14"); // default
  const [copySuccess, setCopySuccess] = useState(false);

  const [email, setEmail] = useState("");
  const [sharedUsers, setSharedUsers] = useState([]);

  // Dropdown for expiry. We'll reuse it for both "no link" and "update" scenarios.
  const expiryOptions = [
    { label: "1 day", value: "1" },
    { label: "7 days", value: "7" },
    { label: "14 days", value: "14" },
    { label: "28 days", value: "28" },
    { label: "Always", value: "always" },
  ];

  // Fetch existing link & shared users on mount
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // 1) Shared users (optional)
        const users = await fetchSharedUsers(surveyId);
        setSharedUsers(users.filter((user) => user.type === "email"));

        // 2) Latest shared link
        try {
          const data = await getLatestSharedLink(surveyId);
          if (data?.link) {
            setLink(data.link);
            setExpiresAt(data.expiresAt);
          }
        } catch (err) {
          if (err?.response?.status === 404) {
            console.warn("No shared link found for this survey.");
          } else {
            console.error("Failed to fetch shared link:", err.message);
            toast.error("Failed to fetch shared link.");
          }
        }
      } catch (error) {
        console.error("Failed to fetch shared data:", error.message);
        toast.error("Failed to fetch shared data.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [surveyId]);

  /**
   * Generate or Update the shared link with the chosen expiry.
   */
  const handleGenerateOrUpdateLink = async () => {
    setLoading(true);
    try {
      const response = await generateSharedLink(surveyId, expiryChoice);
      if (response?.link) {
        setLink(response.link);
        setExpiresAt(response.expiresAt);
        toast.success("Shareable link generated!");
      }
    } catch (error) {
      console.error("Error generating link:", error.message);
      toast.error("Failed to generate shareable link.");
    } finally {
      setLoading(false);
    }
  };

  /**
   * Copy link to clipboard
   */
  const handleCopyLink = () => {
    if (link) {
      navigator.clipboard.writeText(link);
      setCopySuccess(true);
      toast.success("Link copied to clipboard!");
      setTimeout(() => setCopySuccess(false), 3000);
    }
  };

  /**
   * Simple helper to display the expiry date
   */
  const formatExpiry = (expiresAt) => {
    if (!expiresAt) return "No expiry (Always)";
    return new Date(expiresAt).toLocaleString();
  };

  /**
   * Example "Share via Email" button logic
   */
  const handleSendEmail = () => {
    if (!email.trim()) {
      toast.error("Email address is required.");
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      toast.error("Please enter a valid email address.");
      return;
    }

    toast.success(`Shared with ${email}`);
    setSharedUsers((prev) => [
      ...prev,
      { name: email, sharedAt: new Date().toISOString() },
    ]);
    setEmail("");
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        {/* HEADER */}
        <div className="modal-header">
          <h2>Share Survey</h2>
          <button className="close-button" onClick={onClose}>
            &times;
          </button>
        </div>

        <div className="modal-body">
          {loading && <p>Loading...</p>}

          {/* IF WE DO NOT HAVE A LINK YET */}
          {!link && !loading && (
            <div className="generate-row">
              <label htmlFor="expiry-choice">Link Expiry:</label>
              <select
                id="expiry-choice"
                value={expiryChoice}
                onChange={(e) => setExpiryChoice(e.target.value)}
              >
                {expiryOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>

              <button
                className="generate-link-button"
                onClick={handleGenerateOrUpdateLink}
                disabled={loading}
              >
                Generate/Update Link
              </button>
            </div>
          )}

          {/* IF WE DO HAVE A LINK */}
          {link && (
  <div className="two-row-grid">
    {/* Column 1, Rows 1-2: Copy button */}
    <button
      onClick={handleCopyLink}
      className="copy-link-buttons grid-copy-button"
      disabled={!link}
    >
      {copySuccess ? "Copied!" : <i className="fa-regular fa-copy"></i>}
    </button>

    {/* Column 2, Row 1: Survey Title */}
    <h3 className="survey-title grid-survey-title">
      {surveyTitle || "Your Survey"}
    </h3>

    {/* Column 2, Row 2: "Expires" text */}
    <p className="expiry-line grid-expiry-line">
      Expires: {formatExpiry(expiresAt)}
    </p>

    {/* Column 3, Row 1-2: Expiry dropdown (spans both rows) */}
    <select
      className="expiry-dropdown grid-expiry-dropdown"
      value={expiryChoice}
      onChange={(e) => setExpiryChoice(e.target.value)}
    >
      {expiryOptions.map((opt) => (
        <option key={opt.value} value={opt.value}>
          {opt.label}
        </option>
      ))}
    </select>

    {/* Column 4, Row 1-2: Update Expiry button (spans both rows) */}
    <button
      onClick={handleGenerateOrUpdateLink}
      disabled={loading}
      className="update-expiry-button grid-update-expiry-button"
    >
      Update Expiry
    </button>
  </div>
)}


          {/* EMAIL SHARING SECTION */}
          <div className="email-sharing">
            <h3>Share via Email</h3>
            <div className="email-input-container">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter email address"
              />
              <button
                onClick={handleSendEmail}
                className="send-email-button"
                disabled={!email.trim()}
              >
                Send
              </button>
            </div>
          </div>

          {/* LIST OF SHARED USERS (OPTIONAL) */}
          {sharedUsers.length > 0 && (
            <div className="shared-users">
              <h3>Shared With</h3>
              <ul className="people-list">
                {sharedUsers.map((user, index) => (
                  <li key={index} className="person-item">
                    <div className="person-info">
                      <i className="fa-solid fa-user user-icon"></i>
                      <div className="person-details">
                        <span>{user.name}</span>
                        <span className="access-date">
                          Shared on{" "}
                          {new Date(user.sharedAt).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* FOOTER */}
        <div className="modal-footer">
          <button onClick={onClose} className="close-modal-button">
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default ShareSurveyModal;
